// SQUADS (#7010): an admin names a group of the account's repos, and Fleet Live (#6777,
// #6779) groups its Core Intelligence feed by squad. The design is
// `docs/design/fleet-redesign/squads.md` (D0-D12); this module is its pure half.
//
// - `effectiveSquads` is the ONE place "a repo in no squad is its own squad" (owner ruling
//   G-9) is decided. The implicit squad is DERIVED here and never stored, so a repo leaves
//   its implicit squad the moment it joins a named one and returns the moment it leaves.
// - `parseSquadName` is the ONE name rule (D6). The database restates only its shape
//   (`squads_name_ck`: trimmed, 1-40 characters) as a backstop; the alphabet is refused
//   here, by the control both REST and MCP call, so both doors answer the same 422.
// - The key scheme keeps a named squad and an implicit one apart by PREFIX: `sq_<id>` and
//   `repo_<repoId>`, so a squad uuid equal to a repo uuid still names two squads.

/**
 * The most repo names one squad write may carry (#7465 review): a write resolves its names
 * in ONE batched lookup, and this bounds what one request can ask for. Measured 2026-10-28
 * on prod: the largest account holds 75 repositories (5 accounts), so 200 is well above any
 * real squad while keeping a hostile body from becoming an unbounded `IN` list. A write over
 * it is the 422 `squad_repos_too_many`, refused before any read.
 */
export const SQUAD_REPOS_MAX = 200;

/** A squad name's longest form, in characters (code points) after trimming (D6). */
export const SQUAD_NAME_MAX_CHARS = 40;

/** Unicode letters, digits, spaces, `-` and `_` (D6). Tested on the trimmed value. */
const SQUAD_NAME_ALPHABET = /^[\p{L}\p{N} _-]+$/u;

/** Which part of the rule a refused name broke — the REST 422 / MCP error names it. */
export type SquadNameRefusal = 'empty' | 'too_long' | 'bad_characters';

/** The name rule's answer: the TRIMMED name (what is stored), or the rule it broke. */
export type SquadNameParse =
  | { readonly ok: true; readonly name: string }
  | { readonly ok: false; readonly why: SquadNameRefusal; readonly detail: string };

const SQUAD_NAME_DETAIL: Readonly<Record<SquadNameRefusal, string>> = {
  empty: 'a squad name must have at least one character after trimming',
  too_long: `a squad name must be at most ${SQUAD_NAME_MAX_CHARS} characters after trimming`,
  bad_characters: 'a squad name may hold only letters, digits, spaces, "-" and "_"',
};

/**
 * Trim, then check length and alphabet. Length counts CODE POINTS, as Postgres
 * `char_length` does, so the name this accepts is one `squads_name_ck` accepts.
 */
export function parseSquadName(raw: string): SquadNameParse {
  const name = raw.trim();
  const refuse = (why: SquadNameRefusal): SquadNameParse => ({
    ok: false,
    why,
    detail: SQUAD_NAME_DETAIL[why],
  });
  if (name.length === 0) return refuse('empty');
  if ([...name].length > SQUAD_NAME_MAX_CHARS) return refuse('too_long');
  if (!SQUAD_NAME_ALPHABET.test(name)) return refuse('bad_characters');
  return { ok: true, name };
}

/** A named squad's key: `sq_<squad id>`. */
export function namedSquadKey(squadId: string): string {
  return `sq_${squadId}`;
}

/** An implicit squad's key: `repo_<repo id>` — never equal to a {@link namedSquadKey}. */
export function implicitSquadKey(repoId: string): string {
  return `repo_${repoId}`;
}

/** An implicit squad's name: the repo's name without its owner (`acme/web` -> `web`, D6). */
export function implicitSquadName(fullName: string): string {
  const slash = fullName.indexOf('/');
  return slash === -1 ? fullName : fullName.slice(slash + 1);
}

/** One squad as Fleet Live reads it: a named one, or a repo standing as its own. */
export type EffectiveSquad = {
  readonly key: string;
  readonly name: string;
  readonly repoIds: readonly string[];
  readonly implicit: boolean;
};

/**
 * One entry per named squad that has at least one repo (a squad with none shows no tab
 * until refilled, D8), in the order given, then one implicit entry per repo in `repos`
 * that no named squad holds, in the order given. Which repos count is the caller's: a
 * named squad's members are taken as stored (a disabled member stays, D9), and `repos`
 * is only consulted for the repos left over.
 */
export function effectiveSquads(
  repos: readonly { readonly id: string; readonly fullName: string }[],
  squads: readonly {
    readonly id: string;
    readonly name: string;
    readonly repoIds: readonly string[];
  }[],
): EffectiveSquad[] {
  const grouped = new Set(squads.flatMap((s) => s.repoIds));
  const named = squads
    .filter((s) => s.repoIds.length > 0)
    .map((s) => ({ key: namedSquadKey(s.id), name: s.name, repoIds: s.repoIds, implicit: false }));
  const implicit = repos
    .filter((r) => !grouped.has(r.id))
    .map((r) => ({
      key: implicitSquadKey(r.id),
      name: implicitSquadName(r.fullName),
      repoIds: [r.id],
      implicit: true,
    }));
  return [...named, ...implicit];
}
